<!-- シミュレーション用に標準在庫指数を変更するためのポップアップ -->

<script lang="ts" setup>
import SimpleSelectField from '@/components/input/SimpleSelectField.vue';
import { computed, onMounted, reactive, watch } from 'vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import {
  MAX_OF_CHANGE_DATE,
  MIN_OF_CHANGE_DATE,
  STEP_OF_CHANGE_DATE,
  STEP_OF_CHANGE_MONTH,
  UNIT_OF_DAY,
  UNIT_OF_MONTH_SIMPLE,
  UNIT_OF_WEEK_SIMPLE,
} from '@/coreDomainsModule/constants';
import { StdIndexInfo } from '@/appLogic/interfaces/inventory';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { DateUnitType } from '@/coreDomainsModule/date';
import { i18n } from '@/i18n';

const t = i18n.t;

// props
const props = defineProps<{
  popupState: boolean;
  stdInventoryInfo: StdIndexInfo;
}>();

// emits
const emits = defineEmits<{
  (e: 'onClose'): void;
  (e: 'onConfirm', stdIndexInfo: StdIndexInfo): void;
  (e: 'onConfirmedInvalidValue'): void;
  (e: 'onUpdateStdInventory', stdIndexInfo: StdIndexInfo): void;
}>();

// イベントフック
onMounted(() => {
  state.popupState = props.popupState;
  updateInfo(props.stdInventoryInfo);
});

watch(
  () => props.popupState,
  (newVal) => {
    state.popupState = newVal;
    if (state.popupState) {
      updateInfo(props.stdInventoryInfo);
    }
  }
);

watch(
  () => props.stdInventoryInfo,
  (newVal) => {
    updateInfo(newVal);
  }
);

// computed
// 数値入力のステップ
const stepOfNumberField = computed(() => {
  switch (state.selectedDateUnit) {
    case 'month':
      return STEP_OF_CHANGE_MONTH;
    default:
      return STEP_OF_CHANGE_DATE;
  }
});

// 変数
const state = reactive<{
  popupState: boolean;
  minNumber: number;
  maxNumber: number;
  selectedDateUnit: DateUnitType;
}>({
  popupState: false,
  minNumber: 0,
  maxNumber: 0,
  selectedDateUnit: 'day',
});

// ドロップダウンの選択肢
const options = [
  { label: UNIT_OF_DAY, value: 'day' },
  { label: UNIT_OF_WEEK_SIMPLE, value: 'week' },
  { label: UNIT_OF_MONTH_SIMPLE, value: 'month' },
];

// 関数

/**
 * 表示に使用する情報を更新する
 * @param value 標準在庫指数情報
 */
const updateInfo = (value: StdIndexInfo) => {
  if (value === undefined) {
    return;
  }
  state.minNumber = value.min;
  state.maxNumber = value.max;
  const selectedDateUnit = DateService.convertToIDateUnit(value.unit);
  if (selectedDateUnit !== 'unknown') {
    state.selectedDateUnit = selectedDateUnit;
  } else {
    // NOTO: 単位情報が'unknown'だったらerror出しとく
    const error = new Error(`Error: stdInventory unit is 'unknown'`);
    console.error(error);
  }
};

/**
 * 最小値の変更を受け取る
 * @param event 最小値入力フィールドのonChangeイベント
 */
const onChangeMinNumber = (event: Event) => {
  if (event.target instanceof HTMLInputElement) {
    const minNumber = Number(event.target.value);
    if (minNumber.isValidNumber({})) {
      state.minNumber = minNumber;
    }
  }
};
/**
 * 最大値の変更を受け取る
 * @param event 最大値入力フィールドのonChangeイベント
 */
const onChangeMaxNumber = (event: Event) => {
  if (event.target instanceof HTMLInputElement) {
    const maxNumber = Number(event.target.value);
    if (maxNumber.isValidNumber({})) {
      state.maxNumber = maxNumber;
    }
  }
};
/**
 * 日付単位の変更を受け取る
 * @param unit 日付単位
 */
const onChangeDateUnit = (unit) => {
  if (unit !== undefined) {
    state.selectedDateUnit = unit;
  }
};

/**
 * 最小値と最大値を丸める
 * @param min 最小値
 * @param max 最大値
 * @param selectedUnit 日付単位
 * @returns 丸めた最小値と最大値 or エラー
 */
const roundMinMaxValues = (min: number, max: number, selectedUnit: DateUnitType): { min: number; max: number } | Error => {
  // 最大値と最小値の間に収める
  min = min.clamp(MIN_OF_CHANGE_DATE, MAX_OF_CHANGE_DATE);
  max = max.clamp(MIN_OF_CHANGE_DATE, MAX_OF_CHANGE_DATE);
  if (min > max) {
    // 最小値が最大値より大きい場合は、無効な入力として呼び出し元に知らせる
    return new Error('Error: min is greater than max');
  }
  // 必要に応じて小数点以下を切り捨てる
  min = DateService.roundDisplayDate(min, selectedUnit);
  max = DateService.roundDisplayDate(max, selectedUnit);
  return { min, max };
};

/**
 * 確定ボタン押下
 */
const onConfirm = () => {
  const displayValue = roundMinMaxValues(state.minNumber, state.maxNumber, state.selectedDateUnit);
  if (displayValue instanceof Error) {
    // 無効な入力として呼び出し元に知らせる
    emits('onConfirmedInvalidValue');
    console.error(displayValue);
    return;
  }
  emits('onConfirm', {
    name: props.stdInventoryInfo.name,
    min: displayValue.min,
    max: displayValue.max,
    unit: state.selectedDateUnit,
  });
};

/**
 * DB上の標準在庫指数の情報を更新する
 */
const onUpdateStdInventory = () => {
  const displayValue = roundMinMaxValues(state.minNumber, state.maxNumber, state.selectedDateUnit);
  if (displayValue instanceof Error) {
    // 無効な入力として呼び出し元に知らせる
    emits('onConfirmedInvalidValue');
    console.error(displayValue);
    return;
  }
  emits('onUpdateStdInventory', {
    name: props.stdInventoryInfo.name,
    min: displayValue.min,
    max: displayValue.max,
    unit: state.selectedDateUnit,
  });
};
</script>

<template>
  <div class="main-area">
    <div v-show="state.popupState" class="outside-area" @click="emits('onClose')" />
    <div v-if="state.popupState" class="main-area__main-content main-content">
      <div class="main-content__content-container content-container">
        <div class="content-container__header">
          <div class="content-container__title">{{ t('標準在庫指数') }}</div>
          <!-- DB上の標準在庫指数を更新する -->
          <TextButton
            :button-style="'btn-secondary'"
            :size="'btn-ss'"
            class="content-container__update-default"
            @click="onUpdateStdInventory"
          >
            {{ t('デフォルト値更新:1') }}<br />{{ t('デフォルト値更新:2') }}
          </TextButton>
        </div>
        <div class="content-container__edit-field-container edit-field-container">
          <div class="number-field-container">
            <div class="number-field-container__min-title">{{ t('Min') }}</div>
            <div class="number-field-container__max-title">{{ t('Max') }}</div>
            <div class="number-field-container__number-field">
              <input
                class="number-field"
                type="number"
                :value="state.minNumber"
                :step="stepOfNumberField"
                :min="MIN_OF_CHANGE_DATE"
                :max="state.maxNumber"
                @change="onChangeMinNumber"
              />
            </div>
            <div class="number-field-container__separator">ー</div>
            <div class="number-field-container__number-field">
              <input
                class="number-field"
                type="number"
                :value="state.maxNumber"
                :step="stepOfNumberField"
                :min="state.minNumber"
                :max="MAX_OF_CHANGE_DATE"
                @change="onChangeMaxNumber"
              />
            </div>
          </div>
          <SimpleSelectField
            class="edit-field-container__date-unit"
            :options="options"
            :selected-value="state.selectedDateUnit"
            @update:model-value="onChangeDateUnit"
          />
        </div>
        <div class="content-container__button-container button-container">
          <TextButton :button-style="'btn-secondary'" :size="'btn-ss'" class="button-container__button" @click="emits('onClose')">
            {{ t('キャンセル') }}
          </TextButton>
          <TextButton :color="'primary'" :size="'btn-ss'" class="button-container__button" @click="onConfirm">
            {{ t('確定') }}
          </TextButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.main-area {
  &__main-content {
    position: fixed;
    z-index: 100;
    right: 16px;
  }
}
.outside-area {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: $transparency0;
}
.main-content {
  border-radius: 8px;
  border-width: 1px;
  border-color: $border;
  background-color: $semanticWhite;
  box-shadow: $boxShadowM;
  &__content-container {
    margin: 16px;
  }
}
.content-container {
  display: grid;
  grid-template-rows: minmax(20px, 1fr) minmax(20px, 2fr) minmax(20px, 1fr);
  gap: 12px;
  &__title {
    font-size: $fontSizeM;
    font-weight: bold;
    text-align: left;
    margin-top: auto;
    margin-bottom: auto;
  }

  &__header {
    display: grid;
    grid-template-columns: 1fr auto;
    justify-content: space-between;
    gap: 8px;
  }

  &__update-default {
    height: auto;
    padding: 4px 8px;
  }
}
.edit-field-container {
  display: grid;
  grid-template-columns: repeat(2, auto);
  gap: 8px;
  align-items: end;
  &__date-unit {
    min-width: 40px;
  }
}
.number-field-container {
  display: grid;
  grid-template-columns: repeat(3, auto);
  grid-template-rows: repeat(2, auto);
  gap: 8px;
  align-items: center;
  &__min-title {
    grid-column-start: 1;
    grid-column-end: 2;
    text-align: center;
    margin: auto;
  }
  &__max-title {
    grid-column-start: 3;
    grid-column-end: 4;
    text-align: center;
    margin: auto;
  }
  &__separator {
    text-align: center;
    margin: auto;
  }
}
.button-container {
  text-align: right;
  &__button {
    margin-left: 8px;
  }
}
.number-field {
  width: 52px;
  font-size: $fontSizeM;
}
</style>
